import './App.css';
import {useCallback, useEffect, useState} from 'react';
import {apiFetch, getToken, getStoredUsername, setToken, setStoredUsername, setStoredUserId} from './api';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import ChecklistContainer from './components/ChecklistContainer';
import DeleteAccountModal from './components/DeleteAccountModal';

export default function App() {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
    const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
    const [username, setUsername] = useState(getStoredUsername);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

    const toggleTheme = useCallback(() => setTheme(t => {
        const next = t === 'dark' ? 'light' : t === 'light' ? 'minimal' : 'dark';
        localStorage.setItem('theme', next);
        return next;
    }), []);

    useEffect(() => {
        const handleAuthLogout = () => {
            setToken(null);
            setStoredUsername(null);
            setIsAuthenticated(false);
            setUsername('');
        };
        window.addEventListener('auth:logout', handleAuthLogout);
        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, []);

    const handleLogin = useCallback((token, loggedInUsername, userId) => {
        setToken(token);
        setStoredUsername(loggedInUsername);
        setStoredUserId(userId);
        setUsername(loggedInUsername);
        setIsAuthenticated(true);
    }, []);

    const handleLogout = useCallback(async () => {
        await apiFetch('/auth/logout', {method: 'POST'});
        setToken(null);
        setStoredUsername(null);
        setStoredUserId(null);
        setIsAuthenticated(false);
        setUsername('');
    }, []);

    const handleDeleteAccount = useCallback(async () => {
        const res = await apiFetch('/users/me', {method: 'DELETE'});
        if (!res.ok) return;
        setToken(null);
        setStoredUsername(null);
        setStoredUserId(null);
        setIsAuthenticated(false);
        setUsername('');
        setShowDeleteAccountModal(false);
    }, []);

    if (!isAuthenticated) {
        const params = new URLSearchParams(window.location.search);
        const wantsLogin = params.has('signin');
        const wantsRegister = params.has('register');
        if (!wantsLogin && !wantsRegister) {
            return <LandingPage theme={theme} onToggleTheme={toggleTheme}/>;
        }
        return (
            <div className="App" data-theme={theme}>
                <Header theme={theme} onToggleTheme={toggleTheme}/>
                {wantsRegister
                    ? <RegisterScreen onLogin={handleLogin}/>
                    : <LoginScreen onLogin={handleLogin}/>
                }
                <Footer/>
            </div>
        );
    }

    return (
        <div className="App" data-theme={theme}>
            <Header
                theme={theme}
                onToggleTheme={toggleTheme}
                onLogout={handleLogout}
                username={username}
                onDeleteAccount={() => setShowDeleteAccountModal(true)}
            />
            {showDeleteAccountModal ? (
                <DeleteAccountModal
                    onConfirm={handleDeleteAccount}
                    onCancel={() => setShowDeleteAccountModal(false)}
                />
            ) : null}
            <ChecklistContainer/>
            <Footer/>
        </div>
    );
}
